import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Save, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

export default function AdminSettings() {
  const { t } = useLanguage();
  const [codes, setCodes] = useState([]);
  const [siteSettings, setSiteSettings] = useState({ transfer_requires_code: true });
  const [siteLoaded, setSiteLoaded] = useState(false);
  const [siteSaving, setSiteSaving] = useState(false);

  useEffect(() => { (async () => {
    try {
      const r = await api.get('/admin/transfer-codes/default');
      setCodes(r.data.codes || []);
    } catch { /* silent */ }
    try {
      const s = await api.get('/admin/settings/site');
      setSiteSettings({ transfer_requires_code: !!s.data.transfer_requires_code });
    } catch { /* silent */ }
    setSiteLoaded(true);
  })(); }, []);

  const update = (i, k, v) => { const arr = [...codes]; arr[i] = { ...arr[i], [k]: v }; setCodes(arr); };
  const save = async () => {
    if (codes.length !== 5) { toast.error('Vous devez configurer exactement 5 codes'); return; }
    await api.put('/admin/transfer-codes/default', { codes });
    toast.success(t('common.operationSuccess'));
  };

  const toggleRequireCode = async (enabled) => {
    // Optimistic update
    const prev = siteSettings.transfer_requires_code;
    setSiteSettings({ ...siteSettings, transfer_requires_code: !!enabled });
    setSiteSaving(true);
    try {
      await api.put('/admin/settings/transfer-requires-code', { enabled: !!enabled });
      toast.success(t('admin.settingsSaved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
      setSiteSettings({ ...siteSettings, transfer_requires_code: prev });
    } finally {
      setSiteSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold">{t('admin.settingsTitle')}</h1>

      {/* Site-wide settings (Feature v4) */}
      <div className="bg-white rounded-2xl border p-5">
        <h2 className="font-semibold mb-3 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-700"/> {t('admin.siteSettings')}</h2>
        <div className="flex items-start justify-between gap-4 py-2">
          <div className="flex-1">
            <Label htmlFor="toggle-require-code" className="text-base font-medium cursor-pointer">
              {t('admin.requireTransferCode')}
            </Label>
            <p className="text-sm text-slate-500 mt-1">{t('admin.requireTransferCodeHint')}</p>
          </div>
          <Switch
            id="toggle-require-code"
            checked={!!siteSettings.transfer_requires_code}
            disabled={!siteLoaded || siteSaving}
            onCheckedChange={toggleRequireCode}
            data-testid="toggle-transfer-requires-code"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl border p-5">
        <h2 className="font-semibold mb-3">Codes de validation virement par défaut</h2>
        <p className="text-sm text-slate-500 mb-4">Ces 5 codes seront utilisés pour valider chaque virement client (sauf si codes personnalisés).</p>
        <div className="space-y-3">
          {codes.map((c, i) => (
            <div key={i} className="grid md:grid-cols-3 gap-3">
              <div><Label>Code #{c.numero}</Label><Input value={c.code} onChange={(e) => update(i, 'code', e.target.value)} data-testid={`input-default-code-${i}`}/></div>
              <div className="md:col-span-2"><Label>Libellé</Label><Input value={c.libelle} onChange={(e) => update(i, 'libelle', e.target.value)} data-testid={`input-default-libelle-${i}`}/></div>
            </div>
          ))}
        </div>
        <Button onClick={save} className="mt-4 bg-blue-700" data-testid="btn-save-default-codes"><Save className="w-4 h-4 mr-1"/> {t('common.save')}</Button>
      </div>
      <div className="bg-white rounded-2xl border p-5">
        <h2 className="font-semibold mb-3">Configuration SMTP</h2>
        <div className="text-sm text-slate-600 space-y-1">
          <div>Hôte : <span className="font-mono">smtp.ionos.fr</span></div>
          <div>Port : <span className="font-mono">587 (STARTTLS)</span></div>
          <div>Compte : <span className="font-mono">contact@bfigroupefr.com</span></div>
          <div className="text-xs text-slate-500">Ces valeurs sont définies dans les variables d’environnement <code>.env</code> du backend.</div>
        </div>
      </div>
    </div>
  );
}
